// Report.tsx — экран «Отчёт за день» (Ф4). Детализация одного run'а по разделам
// (из /api/report/:runId). Без runId — подсказка выбрать отчёт из «Сегодня»/«Неделя».
// value уже отформатирован бэкендом (formatAnswerValue, §I8 snapshot текста вопроса);
// repeater-вопросы пока не показываются (паритет с ботом, отдельная фаза).

import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { ReportResponse } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { hhmm, humanDate } from '../lib/format';
import { useApi } from '../lib/useApi';

/** Человекочитаемый статус сдачи для подзаголовка. */
function statusLine(r: ReportResponse): string {
  switch (r.status) {
    case 'completed':
      return r.completedAt ? `сдан ${hhmm(r.completedAt)}` : 'сдан';
    case 'abandoned':
      return 'не сдан';
    case 'in_progress':
      return 'заполняется';
    default:
      return 'не начат';
  }
}

/** Класс окраски ответа: «Да» → зелёный, «Нет» → красный (как в мокапе). */
function answerClass(value: string | null): string {
  if (value === 'Да') return 'a yes';
  if (value === 'Нет') return 'a no';
  return 'a';
}

function ReportView({ data }: { data: ReportResponse }) {
  const navigate = useNavigate();
  const day = data.scheduledForDate;

  return (
    <section>
      <button type="button" className="back" onClick={() => navigate('/')}>
        ← к дашборду
      </button>
      <div className="ph">
        <h1>Отчёт за день — {data.respondent.name}</h1>
      </div>
      <p className="sub">
        {data.survey.title} · {humanDate(day)} · {statusLine(data)} · {data.sections.length} разд.
      </p>

      {data.sections.length === 0 && <StateCard kind="empty" message="В отчёте нет ответов." />}

      {data.sections.map((section, idx) => (
        <details className="sec" key={section.sectionId} open={idx === 0}>
          <summary>
            📌 {section.title}
            <span className="chev">›</span>
          </summary>
          <div className="body">
            {section.answers.map((a) => (
              <div className="qa" key={a.questionId}>
                <div className="q">{a.questionText}</div>
                <div className={answerClass(a.value)}>
                  {a.value ?? <span style={{ color: 'var(--muted)' }}>—</span>}
                </div>
              </div>
            ))}
          </div>
        </details>
      ))}

      <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
        <a className="btn" href={api.exportUrl(day, day)}>
          ⬇ Скачать XLSX
        </a>
        <button type="button" className="btn ghost" onClick={() => window.print()}>
          Печать
        </button>
        <button type="button" className="btn ghost" onClick={() => navigate('/week')}>
          Все дни →
        </button>
      </div>
    </section>
  );
}

function ReportHeader({ children }: { children: ReactNode }) {
  return (
    <section>
      <div className="ph">
        <h1>Отчёт за день</h1>
      </div>
      {children}
    </section>
  );
}

function ReportLoader({ runId }: { runId: string }) {
  const { data, loading, error } = useApi<ReportResponse>(() => api.report(runId), [runId]);

  if (loading) return <ReportHeader><StateCard kind="loading" message="Загрузка…" /></ReportHeader>;
  if (error || !data) {
    return <ReportHeader><StateCard kind="error" message={error ?? 'Отчёт не найден'} /></ReportHeader>;
  }
  return <ReportView data={data} />;
}

export default function Report() {
  const { runId } = useParams<{ runId: string }>();

  if (!runId) {
    return (
      <ReportHeader>
        <p className="sub">Детализация отчёта по разделам — читаемый аналог /report.</p>
        <StateCard
          kind="empty"
          message="Выберите отчёт на экране «Сегодня» или «Неделя», чтобы открыть детализацию."
        />
      </ReportHeader>
    );
  }

  return <ReportLoader runId={runId} />;
}
